import React, { useState, useEffect } from 'react';
import { useCart } from '@/contexts/CartContext';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { getCustomerId } from '@/utils/customerIdentifier'
import { addItemsToTableSessionOrCreateOrder } from '@/services/menuService'
import { usePublicDeliveryZones } from '@/hooks/useDeliveryZones';
import { useCanCreateOrder } from '@/hooks/useUsageMetrics';
import type { OrderType, DeliveryZone } from '@/types/index';
import { NetworkTestDialog } from '@/components/NetworkTestDialog';
import { CartLoyaltySection } from '@/components/CartLoyaltySection';
import { CartSuggestions } from '@/components/CartSuggestions';
import { AnimatePresence } from 'framer-motion';

// New Components
import { CartItem } from './cart/CartItem';
import { CartSummary } from './cart/CartSummary';
import { CartForm } from './cart/CartForm';
import { CartStickyFooter } from './cart/CartStickyFooter';

interface CartProps {
  branchId: string;
  currency?: string;
}

export const Cart: React.FC<CartProps> = ({ branchId, currency = 'USD' }) => {
  const { items, removeItem, updateQuantity, clearCart, totalAmount } = useCart();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [restaurantId, setRestaurantId] = useState<string>('');
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const { deliveryZones, loading: zonesLoading } = usePublicDeliveryZones(branchId);
  
  // Check if branch can accept new orders (subscription limit check)
  const { canCreate: canCreateOrder, isLoading: isCheckingOrderLimit, message: orderLimitMessage } = useCanCreateOrder(branchId);

  // Fetch restaurant_id from branch and ensure cart isolation
  useEffect(() => {
    // Clear any cart persisted under a different branch scope
    try {
      const keys = Object.keys(localStorage)
      keys.forEach(k => {
        if (k.startsWith('restaurant-cart-') && k !== `restaurant-cart-${branchId}`) localStorage.removeItem(k)
      })
    } catch {}

    const fetchRestaurantId = async () => {
      try {
        // Fetch restaurant_id directly from branch data via Supabase
        const { data: branchData, error } = await supabase
          .from('branches')
          .select('restaurant_id')
          .eq('id', branchId)
          .single();

        if (!error && branchData?.restaurant_id) {
          setRestaurantId(branchData.restaurant_id);
        }
      } catch (error) {
        // Non-blocking; restaurant id is only used for loyalty logging
        console.warn('Failed to fetch restaurant ID for loyalty:', error);
      }
    };
    
    fetchRestaurantId();
  }, [branchId]);
  
  const [orderType, setOrderType] = useState<OrderType>('pickup');
  const [selectedDeliveryZone, setSelectedDeliveryZone] = useState<DeliveryZone | null>(null);
  const [isTableFromQR, setIsTableFromQR] = useState(false); // Track if table number is from QR code
  const [loyaltyDiscount, setLoyaltyDiscount] = useState(0);
  const [loyaltyPointsUsed, setLoyaltyPointsUsed] = useState(0);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    address: '',
    tableNumber: '',
    notes: ''
  });

  // Check URL parameters for table number and order type
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tableParam = urlParams.get('table');
    const orderTypeParam = urlParams.get('orderType');

    if (tableParam) {
      setCustomerInfo(prev => ({ ...prev, tableNumber: tableParam }));
      setIsTableFromQR(true); // Mark as coming from QR code
    }

    if (orderTypeParam && (orderTypeParam === 'pickup' || orderTypeParam === 'delivery' || orderTypeParam === 'dine_in')) {
      setOrderType(orderTypeParam as OrderType);
    }
  }, []);

  // Calculate delivery fee and total with delivery
  const deliveryFee = orderType === 'delivery' && selectedDeliveryZone ? selectedDeliveryZone.delivery_fee : 0;
  const subtotalWithDelivery = totalAmount + deliveryFee;
  const finalTotal = subtotalWithDelivery - loyaltyDiscount;

  const handleLoyaltyRedemption = (discount: number, pointsUsed: number) => {
    setLoyaltyDiscount(discount);
    setLoyaltyPointsUsed(pointsUsed);
  };

  const handleLoyaltyTransaction = async (orderId: string, orderAmount: number) => {
    try {
      const customerId = getCustomerId();

      // Simplified loyalty handling to avoid TypeScript errors
      // This can be enhanced once database types are updated
      console.log('Loyalty transaction processed for order:', orderId, 'amount:', orderAmount);
      
    } catch (error) {
      console.error('Error processing loyalty transaction:', error);
    }
  };

  // Check minimum order requirement for delivery
  const meetsMinimumOrder = orderType !== 'delivery' || !selectedDeliveryZone || totalAmount >= selectedDeliveryZone.minimum_order_amount;

  useEffect(() => {
    // Reset delivery zone when order type changes
    if (orderType !== 'delivery') {
      setSelectedDeliveryZone(null);
    }
  }, [orderType]);

  const handleSubmitOrder = async () => {
    // Check subscription order limit first
    if (!canCreateOrder) {
      toast({
        title: t('cart.order_limit_reached_title'),
        description: orderLimitMessage || t('cart.order_limit_reached_desc'),
        variant: "destructive",
      });
      return;
    }
    
    // Basic validation
    const isTableQRDineIn = orderType === 'dine_in' && isTableFromQR
    if (!isTableQRDineIn && (!customerInfo.name || !customerInfo.phone)) {
      toast({
        title: t('cart.missing_info_title'),
        description: t('cart.missing_info_desc'),
        variant: "destructive",
      });
      return;
    }

    // Order type specific validation
    if (orderType === 'dine_in' && !customerInfo.tableNumber) {
      toast({
        title: t('cart.missing_info_title'),
        description: t('cart.missing_table_number'),
        variant: "destructive",
      });
      return;
    }

    if (orderType === 'delivery') {
      if (!customerInfo.address) {
        toast({
          title: t('cart.missing_info_title'),
          description: t('cart.missing_delivery_address'),
          variant: "destructive",
        });
        return;
      }

      if (!selectedDeliveryZone) {
        toast({
          title: t('cart.missing_info_title'),
          description: t('cart.missing_delivery_zone'),
          variant: "destructive",
        });
        return;
      }

      if (!meetsMinimumOrder) {
        toast({
          title: t('cart.missing_info_title'),
          description: t('cart.delivery_minimum_not_met'),
          variant: "destructive",
        });
        return;
      }
    }

    if (items.length === 0) {
      toast({
        title: t('cart.empty_cart_title'),
        description: t('cart.empty_cart_desc'),
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const customerId = getCustomerId();
      
      // Calculate estimated time once
      let estimatedTime: string | undefined = undefined;
      if (orderType === 'delivery' && selectedDeliveryZone) {
        const estimatedDate = new Date();
        estimatedDate.setMinutes(estimatedDate.getMinutes() + selectedDeliveryZone.estimated_delivery_time_minutes);
        estimatedTime = estimatedDate.toISOString();
      } else if (orderType === 'pickup') {
        const estimatedDate = new Date();
        estimatedDate.setMinutes(estimatedDate.getMinutes() + 15); // Default 15 minutes for pickup
        estimatedTime = estimatedDate.toISOString();
      }
      
      // Fetch fresh prices from DB to avoid serving stale cart prices from localStorage
      const menuItemIds = [...new Set(items.map(item => item.menuItem.id))];
      const variantIds = items.filter(item => item.variant?.id).map(item => item.variant!.id);

      const [{ data: freshItems }, { data: freshVariants }] = await Promise.all([
        supabase.from('menu_items').select('id, base_price').in('id', menuItemIds),
        variantIds.length > 0
          ? supabase.from('menu_item_variants').select('id, price').in('id', variantIds)
          : Promise.resolve({ data: [] as { id: string; price: number }[] }),
      ]);

      const itemPriceMap = new Map((freshItems ?? []).map(mi => [mi.id, Number(mi.base_price)]));
      const variantPriceMap = new Map((freshVariants ?? []).map(v => [v.id, Number(v.price)]));

      const orderData = {
        branchId,
        customerName: customerInfo.name,
        customerPhone: customerInfo.phone,
        customerEmail: undefined,
        customerAddress: customerInfo.address || undefined,
        notes: customerInfo.notes,
        customerId,
        orderType,
        tableNumber: orderType === 'dine_in' ? customerInfo.tableNumber : undefined,
        deliveryZoneId: orderType === 'delivery' ? selectedDeliveryZone?.id : undefined,
        deliveryFee,
        estimatedDeliveryTime: estimatedTime,
        items: items.map(item => ({
          menuItemId: item.menuItem.id,
          variantId: item.variant?.id,
          quantity: item.quantity,
          unitPrice: item.variant?.id
            ? (variantPriceMap.get(item.variant.id) ?? item.price)
            : (itemPriceMap.get(item.menuItem.id) ?? item.price),
          notes: item.notes || '',
        })),
      };
      
      // Use the table session-aware order creation function
      const order = await addItemsToTableSessionOrCreateOrder(orderData);

      // Handle loyalty points after successful order (non-blocking)
      if (restaurantId && (loyaltyPointsUsed > 0 || subtotalWithDelivery > 0)) {
        // Don't await this to avoid blocking the order completion
        handleLoyaltyTransaction(order.id, subtotalWithDelivery).catch(error => {
          console.error('Loyalty transaction failed:', error);
          // Don't show error to user as order was successful
        });
      }

      toast({
        title: t('cart.order_success_title'),
        description: `${t('orders.order_number')} ${order.order_number}`,
        className: "bg-green-50 border-green-200 text-green-800",
      });

      clearCart();
      setCustomerInfo({ name: '', phone: '', address: '', tableNumber: '', notes: '' });
      setOrderType('pickup');
      setSelectedDeliveryZone(null);
      setLoyaltyDiscount(0);
      setLoyaltyPointsUsed(0);

    } catch (error: unknown) {
      console.error('Error submitting order:', error);
      
      let errorMessage = t('cart.order_error_desc');
      let errorDetails = '';
      let errorTitle = t('cart.order_error_title');
      
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // Handle subscription limit errors from server
        if (error.message.includes('ORDER_LIMIT_REACHED') || error.message.includes('order limit')) {
          errorTitle = t('cart.order_limit_reached_title');
          errorMessage = t('cart.order_limit_reached_desc');
          errorDetails = '';
        } else if (error.message.includes('SUBSCRIPTION_INACTIVE')) {
          errorTitle = t('cart.order_limit_reached_title');
          errorMessage = t('cart.order_limit_reached_desc');
          errorDetails = '';
        } else if (error.message.includes('Failed to create order')) {
          errorDetails = 'Database connection issue. Please check your internet connection and try the "Test Connection" button below.';
        } else if (error.message.includes('network')) {
          errorDetails = 'Network error. Please check your internet connection and try the "Test Connection" button below.';
        } else if (error.message.includes('permission')) {
          errorDetails = 'Permission denied. Please refresh the page and try again.';
        } else if (error.message.includes('timeout')) {
          errorDetails = 'Connection timeout. Your internet may be slow. Try the "Test Connection" button below.';
        } else if (error.message.includes('fetch')) {
          errorDetails = 'Cannot reach servers. Please check your internet connection and try the "Test Connection" button below.';
        } else {
          errorDetails = `Error details: ${error.message}. Try the "Test Connection" button below to diagnose the issue.`;
        }
      }
      
      toast({
        title: errorTitle,
        description: errorDetails ? `${errorMessage}. ${errorDetails}` : errorMessage,
        variant: "destructive",
        duration: 8000, // Show longer for error messages
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-12 text-center px-4">
        <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mb-4">
          <AlertTriangle className="h-10 w-10 text-gray-300" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">{t('cart.empty_cart_title')}</h3>
        <p className="text-gray-500 max-w-xs mx-auto">{t('cart.empty_cart_desc')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50/50" dir={i18n.dir()}>
      <div className="flex-1 overflow-y-auto pb-24 md:pb-0">
        {/* Order limit warning */}
        {!canCreateOrder && !isCheckingOrderLimit && (
          <Alert variant="destructive" className="mb-4 mx-4 mt-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {orderLimitMessage || t('cart.order_limit_reached_desc')}
            </AlertDescription>
          </Alert>
        )}
        
        <div className="p-4 space-y-6">
          {/* Cart Items */}
          <div className="space-y-1">
            <AnimatePresence initial={false}>
              {items.map((item) => (
                <CartItem
                  key={item.menuItem.id + (item.variant ? '-' + item.variant.id : '')}
                  item={item}
                  currency={currency}
                  onUpdateQuantity={(qty) => updateQuantity(item.menuItem.id, qty, item.variant?.id)}
                  onRemove={() => removeItem(item.menuItem.id, item.variant?.id)}
                />
              ))}
            </AnimatePresence>
          </div>

          {/* Suggestions */}
          <CartSuggestions branchId={branchId} />

          {/* Order Form */}
          <CartForm
            orderType={orderType}
            setOrderType={setOrderType}
            isTableFromQR={isTableFromQR}
            customerInfo={customerInfo}
            setCustomerInfo={setCustomerInfo}
            deliveryZones={deliveryZones || []}
            selectedDeliveryZone={selectedDeliveryZone}
            setSelectedDeliveryZone={setSelectedDeliveryZone}
            zonesLoading={zonesLoading}
            currency={currency}
            totalAmount={totalAmount}
          />

          {/* Loyalty Program Section */}
          {restaurantId && (
            <CartLoyaltySection 
              restaurantId={restaurantId}
              totalAmount={subtotalWithDelivery}
              onPointsRedemption={handleLoyaltyRedemption}
              currency={currency}
            />
          )}

          {/* Order Summary */}
          <CartSummary
            subtotal={totalAmount}
            deliveryFee={deliveryFee}
            loyaltyDiscount={loyaltyDiscount}
            total={finalTotal}
            currency={currency}
          />

          {/* Network Test (Dev only) */}
          {import.meta.env.DEV && (
            <div className="pt-4 pb-8">
              <div className="flex justify-center">
                <NetworkTestDialog />
              </div>
              <p className="text-xs text-gray-500 text-center mt-2">
                هل تواجه مشاكل في الاتصال؟ استخدم زر "اختبار الاتصال" أعلاه لتشخيص مشاكل الشبكة.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Sticky Footer */}
      <CartStickyFooter
        total={finalTotal}
        currency={currency}
        onCheckout={handleSubmitOrder}
        disabled={isSubmitting || !meetsMinimumOrder || !canCreateOrder || isCheckingOrderLimit}
        isSubmitting={isSubmitting}
        itemCount={items.reduce((acc, item) => acc + item.quantity, 0)}
      />
    </div>
  );
};
